'use client';

import React from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';

export interface InitialAvatarProps {
  name: string;
  size?: number;
  fontSize?: number;
  backgroundColor?: string;
  textColor?: string;
  circular?: boolean;
}

const AvatarContainer = styled.div<{ 
  $size: number; 
  $backgroundColor: string; 
  $circular: boolean;
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: ${({ $circular }) => $circular ? '50%' : '8px'};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const InitialsText = styled.span<{ $fontSize: number; $textColor: string }>`
  font-size: ${({ $fontSize }) => $fontSize}px;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  text-transform: uppercase;
  user-select: none;
`;

/**
 * Generate initials from organization name
 * - If multiple words: first letter of each word (up to 2)
 * - If single word: first two letters
 */
const getInitials = (name: string): string => {
  if (!name?.trim()) return '';
  
  const words = name.trim().split(/\s+/);
  
  if (words.length >= 2) {
    // Multiple words: first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1 && words[0].length >= 2) {
    // Single word: first two letters
    return words[0].substring(0, 2).toUpperCase();
  } else if (words.length === 1 && words[0].length === 1) {
    // Single character
    return words[0].toUpperCase();
  }
  
  return '';
};

/**
 * InitialAvatar Component
 * 
 * Displays organization initials in a styled container
 * Used as fallback when no logo is uploaded
 */
export const InitialAvatar: React.FC<InitialAvatarProps> = ({
  name,
  size = 120,
  fontSize = 48,
  backgroundColor = theme.colors.gray[900],
  textColor = theme.colors.white,
  circular = true,
}) => {
  const initials = getInitials(name);

  if (!initials) {
    return null;
  }

  return (
    <AvatarContainer 
      $size={size} 
      $backgroundColor={backgroundColor}
      $circular={circular}
    >
      <InitialsText $fontSize={fontSize} $textColor={textColor}>
        {initials}
      </InitialsText>
    </AvatarContainer>
  );
};
